import { getNotionalCurrencies } from '@src/components/OMS/ManualDealTradeView/utils';
import { fixedRateLoanFieldDefinitions } from '@src/utils/OTC/fixedRateLoanFieldDefinitions';
import { floatingRateLoanFieldDefinitions } from '@src/utils/OTC/floatingRateLoanFieldDefinitions';
import {
  formatLoanSecurityTypeDisplay,
  isUnsecuredLoanSecurityType,
  resolveLoanCollateralAssetDisplay,
} from '@src/utils/OTC/loanSecurity';
import { otcForwardFieldDefinitions } from '@src/utils/OTC/otcForwardFieldDefinitions';
import { otcOptionFieldDefinitions } from '@src/utils/OTC/otcOptionFieldDefinitions';
import {
  formatDayCountConvention,
  formatLoanPaymentType,
  formatPaymentPeriod,
  formatProductType,
} from '@src/utils/OTC/otcUtils';
import { repoFieldDefinitions } from '@src/utils/OTC/repoFieldDefinitions';
import {
  assertNever,
  BoolEnum,
  formatExtendedDurationValue,
  isFixedRateLoanSecurity,
  isFloatingRateLoanSecurity,
  isOTCForwardSecurity,
  isOTCOptionSecurity,
  isOTCRepoSecurity,
  LoanQuoteTerminationTypeEnum,
  StrikeTypeEnum,
  type Currency,
  type LoanSecurityTypeEnum,
  type OTCFixedRateLoanSecurity,
  type OTCFloatingRateLoanSecurity,
  type OTCForwardSecurity,
  type OTCOptionSecurity,
  type OTCRepoSecurity,
  type Security,
} from '@talos/kyoko';

export const ProductTemplateTooltipRowType = {
  Text: 'text',
  Asset: 'asset',
  Number: 'number',
} as const;
export type ProductTemplateTooltipRowType =
  (typeof ProductTemplateTooltipRowType)[keyof typeof ProductTemplateTooltipRowType];

export type ProductTemplateTooltipTextEmphasis = 'important' | 'default';

export type ProductTemplateTooltipTextRow = {
  type: typeof ProductTemplateTooltipRowType.Text;
  label: string;
  value: string;
  emphasis?: ProductTemplateTooltipTextEmphasis;
};

export type ProductTemplateTooltipAssetRow = {
  type: typeof ProductTemplateTooltipRowType.Asset;
  label: string;
  value: string;
};

export type ProductTemplateTooltipNumberRow = {
  type: typeof ProductTemplateTooltipRowType.Number;
  label: string;
  value: string;
  currency: string;
  increment?: string;
};

export type ProductTemplateTooltipRow =
  | ProductTemplateTooltipTextRow
  | ProductTemplateTooltipAssetRow
  | ProductTemplateTooltipNumberRow;

export type ProductTemplateTooltipModel = {
  name: string;
  productType: string;
  sections: ProductTemplateTooltipRow[][];
};

function compactRows(rows: Array<ProductTemplateTooltipRow | undefined>): ProductTemplateTooltipRow[] {
  return rows.filter((row): row is ProductTemplateTooltipRow => row != null);
}

function compactSections(sections: ProductTemplateTooltipRow[][]): ProductTemplateTooltipRow[][] {
  return sections.filter(section => section.length > 0);
}

function textRow(
  label: string,
  value: string | undefined,
  emphasis: ProductTemplateTooltipTextEmphasis = 'important'
): ProductTemplateTooltipTextRow | undefined {
  if (value == null || value === '') {
    return undefined;
  }
  if (emphasis === 'default') {
    return { type: ProductTemplateTooltipRowType.Text, label, value, emphasis: 'default' };
  }
  return { type: ProductTemplateTooltipRowType.Text, label, value };
}

function loanCollateralTooltipRows(
  collateralizationLabel: string,
  collateralAssetLabel: string,
  loanSecurityType: LoanSecurityTypeEnum | undefined,
  collateralAsset: string | undefined
): ProductTemplateTooltipRow[] {
  if (isUnsecuredLoanSecurityType(loanSecurityType)) {
    return compactRows([
      textRow(collateralizationLabel, formatLoanSecurityTypeDisplay(loanSecurityType), 'default'),
      textRow(collateralAssetLabel, '-', 'default'),
    ]);
  }
  return compactRows([
    assetRow(collateralAssetLabel, resolveLoanCollateralAssetDisplay(loanSecurityType, collateralAsset)),
  ]);
}

function assetRow(label: string, value: string | undefined): ProductTemplateTooltipAssetRow | undefined {
  if (value == null || value === '') {
    return undefined;
  }
  return { type: ProductTemplateTooltipRowType.Asset, label, value };
}

function numberRow(
  label: string,
  value: string | undefined,
  currency: string | undefined,
  currenciesBySymbol: Map<string, Currency> | undefined
): ProductTemplateTooltipNumberRow | undefined {
  if (value == null || value === '' || !currency) {
    return undefined;
  }
  return {
    type: ProductTemplateTooltipRowType.Number,
    label,
    value,
    currency,
    increment: currenciesBySymbol?.get(currency)?.DefaultIncrement,
  };
}

function durationDisplay(value: string | undefined): string | undefined {
  if (value == null || value === '') {
    return undefined;
  }
  return formatExtendedDurationValue(value) || value;
}

function periodDisplay(value: string | undefined): string | undefined {
  if (value == null || value === '') {
    return undefined;
  }
  return formatPaymentPeriod(value) || value;
}

function termTypeDisplay(termType: string | undefined): string | undefined {
  if (termType == null || termType === '') {
    return undefined;
  }
  if (termType === LoanQuoteTerminationTypeEnum.OpenTerm) {
    return 'Open';
  }
  return termType;
}

function callOptionDisplay(value: string | undefined): string | undefined {
  if (value === BoolEnum.True) {
    return 'Yes';
  }
  if (value === BoolEnum.False) {
    return 'No';
  }
  return undefined;
}

function optionStrikeValueRow(
  details: OTCOptionSecurity['ProductDetails'],
  currenciesBySymbol: Map<string, Currency> | undefined
): ProductTemplateTooltipRow | undefined {
  switch (details.StrikeType) {
    case StrikeTypeEnum.Fixed:
      return numberRow(
        otcOptionFieldDefinitions.strikePrice.title,
        details.StrikePrice,
        details.StrikePriceCcy?.trim() || 'USD',
        currenciesBySymbol
      );
    case StrikeTypeEnum.Delta: {
      const strikeDelta = details.StrikeDelta?.trim();
      return textRow(otcOptionFieldDefinitions.deltaValue.title, strikeDelta ? `${strikeDelta}D` : undefined);
    }
    default:
      return assertNever(details.StrikeType);
  }
}

function getForwardSections(
  security: OTCForwardSecurity,
  currenciesBySymbol: Map<string, Currency> | undefined
): ProductTemplateTooltipRow[][] {
  const details = security.ProductDetails;
  const { unitNotionalCurrency } = getNotionalCurrencies(details.PayoffType, details.BaseAsset, details.QuoteAsset);

  return compactSections([
    compactRows([
      textRow(otcForwardFieldDefinitions.payoffType.title, details.PayoffType),
      assetRow(otcForwardFieldDefinitions.underlying.title, details.BaseAsset),
      assetRow(otcForwardFieldDefinitions.quoteAsset.title, details.QuoteAsset),
      assetRow(otcForwardFieldDefinitions.deliveryAsset.title, details.DeliveryAsset),
      numberRow(
        otcForwardFieldDefinitions.unitNotional.title,
        details.UnitNotionalAmount,
        unitNotionalCurrency,
        currenciesBySymbol
      ),
      textRow(otcForwardFieldDefinitions.tenor.title, durationDisplay(details.Tenor)),
    ]),
    compactRows([textRow(otcForwardFieldDefinitions.index.title, details.Index)]),
    compactRows([textRow(otcForwardFieldDefinitions.settlementType.title, details.SettlementType)]),
  ]);
}

function getOptionSections(
  security: OTCOptionSecurity,
  currenciesBySymbol: Map<string, Currency> | undefined
): ProductTemplateTooltipRow[][] {
  const details = security.ProductDetails;
  const { unitNotionalCurrency } = getNotionalCurrencies(details.PayoffType, details.BaseAsset, details.QuoteAsset);

  return compactSections([
    compactRows([
      textRow(otcOptionFieldDefinitions.payoffType.title, details.PayoffType),
      assetRow(otcOptionFieldDefinitions.underlying.title, details.BaseAsset),
      assetRow(otcOptionFieldDefinitions.quoteAsset.title, details.QuoteAsset),
      assetRow(otcOptionFieldDefinitions.deliveryAsset.title, details.DeliveryAsset),
      assetRow(otcOptionFieldDefinitions.paymentCcy.title, details.PaymentCcy),
      numberRow(
        otcOptionFieldDefinitions.unitNotional.title,
        details.UnitNotionalAmount,
        unitNotionalCurrency,
        currenciesBySymbol
      ),
      textRow(otcOptionFieldDefinitions.tenor.title, durationDisplay(details.Tenor)),
    ]),
    compactRows([
      optionStrikeValueRow(details, currenciesBySymbol),
      textRow(otcOptionFieldDefinitions.strikeType.title, details.StrikeType),
      textRow(otcOptionFieldDefinitions.exerciseStyle.title, details.ExerciseStyle),
    ]),
    compactRows([textRow(otcOptionFieldDefinitions.index.title, details.Index)]),
    compactRows([textRow(otcOptionFieldDefinitions.settlementType.title, details.SettlementType)]),
  ]);
}

function getFixedRateLoanSections(
  security: OTCFixedRateLoanSecurity,
  currenciesBySymbol: Map<string, Currency> | undefined
): ProductTemplateTooltipRow[][] {
  const details = security.ProductDetails;

  return compactSections([
    compactRows([
      assetRow(fixedRateLoanFieldDefinitions.borrowedAsset.title, details.BorrowedAsset),
      ...loanCollateralTooltipRows(
        fixedRateLoanFieldDefinitions.collateralization.title,
        fixedRateLoanFieldDefinitions.collateralAsset.title,
        details.LoanSecurityType,
        details.CollateralAsset
      ),
      numberRow(
        fixedRateLoanFieldDefinitions.unitLoanAmount.title,
        details.UnitLoanAmount,
        details.BorrowedAsset,
        currenciesBySymbol
      ),
      assetRow(fixedRateLoanFieldDefinitions.loanPaymentCcy.title, details.LoanPaymentCcy),
      textRow(fixedRateLoanFieldDefinitions.term.title, durationDisplay(details.Term)),
      textRow(fixedRateLoanFieldDefinitions.loanPaymentType.title, formatLoanPaymentType(details.LoanPaymentType)),
      textRow(fixedRateLoanFieldDefinitions.paymentPeriod.title, periodDisplay(details.PaymentPeriod)),
      textRow(fixedRateLoanFieldDefinitions.accrualPeriod.title, periodDisplay(details.AccrualPeriod)),
      textRow(
        fixedRateLoanFieldDefinitions.dayCountConvention.title,
        formatDayCountConvention(details.DayCountConvention) || details.DayCountConvention
      ),
    ]),
  ]);
}

function getFloatingRateLoanSections(
  security: OTCFloatingRateLoanSecurity,
  currenciesBySymbol: Map<string, Currency> | undefined
): ProductTemplateTooltipRow[][] {
  const details = security.ProductDetails;

  return compactSections([
    compactRows([
      assetRow(floatingRateLoanFieldDefinitions.borrowedAsset.title, details.BorrowedAsset),
      ...loanCollateralTooltipRows(
        floatingRateLoanFieldDefinitions.collateralization.title,
        floatingRateLoanFieldDefinitions.collateralAsset.title,
        details.LoanSecurityType,
        details.CollateralAsset
      ),
      numberRow(
        floatingRateLoanFieldDefinitions.unitLoanAmount.title,
        details.UnitLoanAmount,
        details.BorrowedAsset,
        currenciesBySymbol
      ),
      assetRow(floatingRateLoanFieldDefinitions.loanPaymentCcy.title, details.LoanPaymentCcy),
      textRow(floatingRateLoanFieldDefinitions.termType.title, termTypeDisplay(details.TermType)),
      textRow(floatingRateLoanFieldDefinitions.term.title, durationDisplay(details.Term)),
      textRow(floatingRateLoanFieldDefinitions.loanPaymentType.title, formatLoanPaymentType(details.LoanPaymentType)),
      textRow(floatingRateLoanFieldDefinitions.paymentPeriod.title, periodDisplay(details.PaymentPeriod)),
      textRow(floatingRateLoanFieldDefinitions.accrualPeriod.title, periodDisplay(details.AccrualPeriod)),
      textRow(
        floatingRateLoanFieldDefinitions.dayCountConvention.title,
        formatDayCountConvention(details.DayCountConvention) || details.DayCountConvention
      ),
    ]),
    compactRows([
      textRow(floatingRateLoanFieldDefinitions.rateSource.title, details.RateSource),
      textRow(floatingRateLoanFieldDefinitions.resetPeriod.title, periodDisplay(details.ResetPeriod)),
    ]),
    compactRows([
      textRow(floatingRateLoanFieldDefinitions.prepaymentOption.title, details.PrepaymentOption),
      textRow(floatingRateLoanFieldDefinitions.callOption.title, callOptionDisplay(details.CallOption)),
    ]),
  ]);
}

function getRepoSections(
  security: OTCRepoSecurity,
  currenciesBySymbol: Map<string, Currency> | undefined
): ProductTemplateTooltipRow[][] {
  const details = security.ProductDetails;

  return compactSections([
    compactRows([
      assetRow(repoFieldDefinitions.repoAsset.title, details.RepoAsset),
      assetRow(repoFieldDefinitions.borrowedAsset.title, details.BorrowedAsset),
      numberRow(
        repoFieldDefinitions.unitRepoAmount.title,
        details.UnitRepoAmount,
        details.RepoAsset,
        currenciesBySymbol
      ),
      textRow(repoFieldDefinitions.termType.title, termTypeDisplay(details.TermType)),
      textRow(repoFieldDefinitions.term.title, durationDisplay(details.Term)),
      textRow(
        repoFieldDefinitions.dayCountConvention.title,
        formatDayCountConvention(details.DayCountConvention) || details.DayCountConvention
      ),
    ]),
  ]);
}

export function getProductTemplateTooltipModel(
  security: Security,
  currenciesBySymbol?: Map<string, Currency>
): ProductTemplateTooltipModel {
  const name = security.DisplaySymbol ?? security.Symbol ?? '';
  const productType = formatProductType(security.SubProductType);

  if (isOTCForwardSecurity(security)) {
    return { name, productType, sections: getForwardSections(security, currenciesBySymbol) };
  }
  if (isOTCOptionSecurity(security)) {
    return { name, productType, sections: getOptionSections(security, currenciesBySymbol) };
  }
  if (isFixedRateLoanSecurity(security)) {
    return { name, productType, sections: getFixedRateLoanSections(security, currenciesBySymbol) };
  }
  if (isFloatingRateLoanSecurity(security)) {
    return { name, productType, sections: getFloatingRateLoanSections(security, currenciesBySymbol) };
  }
  if (isOTCRepoSecurity(security)) {
    return { name, productType, sections: getRepoSections(security, currenciesBySymbol) };
  }

  return { name, productType, sections: [] };
}

export function hasProductTemplateTooltipSections(
  security: Security,
  currenciesBySymbol?: Map<string, Currency>
): boolean {
  return getProductTemplateTooltipModel(security, currenciesBySymbol).sections.length > 0;
}
